import * as THREE from 'three';

export const POWERUP_NONE=0;
export const POWERUP_NITRO=1;
export const POWERUP_SHIELD=2;
export const POWERUP_SHOCKWAVE=3;
export const POWERUP_HOMING=4;

export const POWERUP_DURATION=5;
export const POWERUP_REVEAL_DURATION=.7;
export const POWERUP_RESPAWN_DURATION=12;
export const POWERUP_PICKUP_RADIUS=1.15;
export const POWERUP_SHOCKWAVE_RADIUS=8;
export const POWERUP_HOMING_CONE=Math.cos(THREE.MathUtils.degToRad(25));
export const POWERUP_HOMING_TURN_RATE=THREE.MathUtils.degToRad(92);

export const POWERUP_DEFINITIONS=Object.freeze({
  [POWERUP_NITRO]:Object.freeze({id:'nitro',label:'SOBRECARGA DE NITRO',shortLabel:'SOBRECARGA',symbol:'N',color:'#ffb735'}),
  [POWERUP_SHIELD]:Object.freeze({id:'shield',label:'BURBUJA PROTECTORA',shortLabel:'BURBUJA',symbol:'B',color:'#61efff'}),
  [POWERUP_SHOCKWAVE]:Object.freeze({id:'shockwave',label:'ONDA DE CHOQUE',shortLabel:'ONDA',symbol:'O',color:'#cf8cff'}),
  [POWERUP_HOMING]:Object.freeze({id:'homing',label:'MUNICIÓN TELEDIRIGIDA',shortLabel:'TELEDIRIGIDOS',symbol:'M',color:'#ff657c'}),
});

export function resetVehiclePowerups(vehicle){
  vehicle.powerupHeld=POWERUP_NONE;
  vehicle.powerupPending=POWERUP_NONE;
  vehicle.powerupRevealTimer=0;
  vehicle.nitroOverchargeTimer=0;
  vehicle.powerupShieldTimer=0;
  vehicle.homingAmmoTimer=0;
}

export function tickVehiclePowerups(vehicle,dt){
  vehicle.powerupRevealTimer=Math.max(0,(vehicle.powerupRevealTimer||0)-dt);
  vehicle.nitroOverchargeTimer=Math.max(0,(vehicle.nitroOverchargeTimer||0)-dt);
  vehicle.powerupShieldTimer=Math.max(0,(vehicle.powerupShieldTimer||0)-dt);
  vehicle.homingAmmoTimer=Math.max(0,(vehicle.homingAmmoTimer||0)-dt);
  if(vehicle.powerupRevealTimer<=0&&vehicle.powerupPending){
    vehicle.powerupHeld=vehicle.powerupPending;
    vehicle.powerupPending=POWERUP_NONE;
    return vehicle.powerupHeld;
  }
  return POWERUP_NONE;
}

export function randomPowerup(previous=POWERUP_NONE,random=Math.random){
  const available=[POWERUP_NITRO,POWERUP_SHIELD,POWERUP_SHOCKWAVE,POWERUP_HOMING]
    .filter((id)=>id!==previous);
  return available[Math.floor(Math.max(0,Math.min(.999999,random()))*available.length)];
}

export function collectPowerup(vehicle,powerup,crate){
  if(!vehicle||vehicle.disabled||vehicle.powerupHeld||vehicle.powerupPending||!crate?.available)return false;
  vehicle.powerupPending=powerup;
  vehicle.powerupRevealTimer=POWERUP_REVEAL_DURATION;
  crate.available=false;
  crate.respawnTimer=POWERUP_RESPAWN_DURATION;
  crate.lastPowerup=powerup;
  return true;
}

export function activateHeldPowerup(vehicle){
  const powerup=Number(vehicle?.powerupHeld)||POWERUP_NONE;
  if(!powerup||vehicle.disabled)return POWERUP_NONE;
  vehicle.powerupHeld=POWERUP_NONE;
  if(powerup===POWERUP_NITRO){
    vehicle.nitro=100;
    vehicle.nitroOverchargeTimer=POWERUP_DURATION;
  }else if(powerup===POWERUP_SHIELD){
    vehicle.powerupShieldTimer=POWERUP_DURATION;
  }else if(powerup===POWERUP_HOMING){
    vehicle.homingAmmoTimer=POWERUP_DURATION;
  }
  return powerup;
}

export function shockwaveImpulse(origin,targetPosition,mass=1){
  if(!origin||!targetPosition)return null;
  const offset=targetPosition.clone().sub(origin);
  const distance=offset.length();
  if(distance<=.01||distance>POWERUP_SHOCKWAVE_RADIUS)return null;
  const falloff=1-THREE.MathUtils.clamp(distance/POWERUP_SHOCKWAVE_RADIUS,0,1);
  const shapedFalloff=Math.pow(falloff,.58);
  const massScale=1/Math.sqrt(Math.max(.65,Number(mass)||1));
  const planarStrength=(8+15*shapedFalloff)*massScale;
  const liftStrength=(4.2+4.3*shapedFalloff)*massScale;
  offset.y=0;
  if(offset.lengthSq()<.0001)offset.set(1,0,0);
  else offset.normalize();
  return {
    distance,
    falloff,
    impulse:offset.multiplyScalar(planarStrength).setY(liftStrength),
  };
}

export function applyShockwaveImpulse(vehicle,origin){
  const result=shockwaveImpulse(origin,vehicle?.car?.position,vehicle?.config?.mass);
  if(!result||!vehicle?.velocity)return null;
  const {impulse}=result;
  vehicle.velocity.x+=impulse.x;
  vehicle.velocity.z+=impulse.z;
  // El umbral de contacto del auto absorbe impulsos verticales pequeños. La
  // onda lo separa físicamente del suelo para que el lanzamiento sea inmediato.
  vehicle.velocity.y=Math.min(11.5,Math.max(impulse.y,vehicle.velocity.y+impulse.y));
  vehicle.grounded=false;
  vehicle.wheelsGrounded=false;
  vehicle.groundedGraceTimer=0;
  vehicle.car.position.y+=.16;
  if(typeof vehicle.applyProjectileImpact==='function'){
    const direction=impulse.clone().normalize();
    const impactPosition=vehicle.car.position.clone().addScaledVector(direction,-.48);
    impactPosition.y-=.12;
    vehicle.applyProjectileImpact(direction,impactPosition,1.6);
  }
  return result;
}

function obstacleBlocksSpawn(obstacle,x,z,margin=1.25){
  if(obstacle.minY>.9||obstacle.maxY<-.1)return false;
  if(obstacle.shape==='circle'){
    const dx=x-obstacle.x;
    const dz=z-obstacle.z;
    return dx*dx+dz*dz<(obstacle.radius+margin)**2;
  }
  return x>obstacle.minX-margin&&x<obstacle.maxX+margin
    &&z>obstacle.minZ-margin&&z<obstacle.maxZ+margin;
}

export function createPowerupCrates(world,{groundHeight=()=>world.groundHeight||0,count=5}={}){
  const candidates=[
    [-.27,-.25],[.27,-.25],[-.27,.25],[.27,.25],[0,0],
    [-.38,0],[.38,0],[0,-.38],[0,.38],[-.15,.06],[.15,-.06],
  ];
  const crates=[];
  for(const [nx,nz] of candidates){
    const x=nx*world.width;
    const z=nz*world.depth;
    if(world.obstacles?.some((obstacle)=>obstacleBlocksSpawn(obstacle,x,z)))continue;
    const point=new THREE.Vector3(x,0,z);
    point.y=groundHeight(point)+.52;
    crates.push({id:`powerup-${crates.length+1}`,x:point.x,y:point.y,z:point.z,available:true,respawnTimer:0,lastPowerup:POWERUP_NONE});
    if(crates.length>=count)break;
  }
  if(!crates.length){
    const point=world.spawn?.clone?.()||new THREE.Vector3();
    point.y=groundHeight(point)+.52;
    crates.push({id:'powerup-1',x:point.x,y:point.y,z:point.z,available:true,respawnTimer:0,lastPowerup:POWERUP_NONE});
  }
  return crates;
}

export function updatePowerupCrates(crates,dt){
  crates.forEach((crate)=>{
    if(crate.available)return;
    crate.respawnTimer=Math.max(0,(crate.respawnTimer||0)-dt);
    if(crate.respawnTimer<=0)crate.available=true;
  });
}

export function powerupTarget(owner,candidates,aimDirection,hasLineOfSight=()=>true,current=null){
  if(!owner||!aimDirection)return null;
  const origin=owner.car.position;
  const aim=aimDirection.clone().normalize();
  let best=null;
  let bestDot=POWERUP_HOMING_CONE;
  let currentDot=-1;
  for(const candidate of candidates){
    if(!candidate||candidate===owner||candidate.disabled)continue;
    if(owner.teamId&&candidate.teamId===owner.teamId)continue;
    const direction=candidate.car.position.clone().sub(origin);
    direction.y+=(candidate.car.userData?.collisionHeight||.82)*.35;
    if(direction.lengthSq()<.01)continue;
    direction.normalize();
    const dot=aim.dot(direction);
    const threshold=candidate===current?POWERUP_HOMING_CONE-.035:POWERUP_HOMING_CONE;
    if(dot<threshold||!hasLineOfSight(owner,candidate))continue;
    if(candidate===current)currentDot=dot;
    if(dot>bestDot){best=candidate;bestDot=dot;}
  }
  return current&&currentDot>=POWERUP_HOMING_CONE-.035&&currentDot+.035>=bestDot?current:best;
}

export function steerHomingVelocity(velocity,position,targetPosition,dt){
  if(!velocity||!position||!targetPosition||velocity.lengthSq()<.0001)return velocity;
  const speed=velocity.length();
  const current=velocity.clone().normalize();
  const desired=targetPosition.clone().sub(position);
  if(desired.lengthSq()<.0001)return velocity;
  desired.normalize();
  const angle=current.angleTo(desired);
  if(angle<.0001)return velocity;
  current.lerp(desired,Math.min(1,POWERUP_HOMING_TURN_RATE*dt/angle)).normalize();
  return velocity.copy(current).multiplyScalar(speed);
}
